import React, { useMemo } from 'react';
import { useGetCurrentUserProfile } from '../../hooks/apolloHooks/useGetCurrentUserProfile';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { useGetOwnerCustomerRoles } from '../../hooks/apolloHooks/useGetOwnerCustomerRoles';
import { GET_CUSTOMER_RIGHTS_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { CurrentUserProfileRole } from '../../integrations/allkort/graphql/interface/CurrentUserProfile';
import { CustomerRightsDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import FetchingAnimation from './FetchingAnimation';
import Tooltip from './Tooltip';

interface StructuredCurrentUserRoles {
	other: CurrentUserProfileRole[];
	write: CurrentUserProfileRole[];
	read: CurrentUserProfileRole[];
}

/**
 * Customer state codes reference
 * This refers to hasBlockingState in RightsWrapper
 *
 * Id      Text
 * 1 Konkurs avslutad
 * 2 Likvidation avslutad
 * 3 Fusion avslutad
 * 4 Bolaget upplöst
 * 5 Kundavslut
 * 6 Avliden - ******** det finns inget konto (kunde inte logga in, kontot finns inte kontakta kundservice) ********
 * 7 Utvandrad
 * 8 Skyddad identitet
 * 9 Tekniskt avregistrerad
 * 10 Inaktiv
 * 11 Konkurs
 * 12 Diverse ärende
 * 13 Avslag
 * 14 Komplettering sök kund
 * 15 Anonymiserad ******** det finns inget konto (kunde inte logga in, kontot finns inte kontakta kundservice) ********
 * 16 Fusion inledd
 * 17 Konkurs ansökan
 * 18 Konkurs beslut
 * 19 Avregistrerad
 * 20 Aktiv
 * 21 Likvidation inledd
 * 22 Likvidation upphört
 * 23 Rekonstruktion upphört
 * 24 Rekonstruktion inledd
 * 25 Likvidation beslutad
 * 26 Kundavslut ej kort
 * 27 Skuldsanering
 * 28 Återkoppling från befintlig kund
 * 29 Övrigt
 * 30 Ej slutbetald
 * 31 Komplettering inkommer
 * 32 F-skatt saknas
 * 33 Kreditkontroll
 * 34 Ny bedömning
 */

/**
 * All Query rights:
"query.currentCustomer"
"query.currentCustomer.userProfiles"
"query.currentCustomer.userProfiles.roles"
"query.currentCustomer.userProfiles.identifier"
"query.currentCustomer.emails"
"query.currentCustomer.phoneNumbers"
"query.currentCustomer.address"
"query.currentCustomer.cards"
"query.currentCustomer.cards.transactions"
"query.currentCustomer.cards.limit"
"query.currentCustomer.transactions"
"query.currentCustomer.balance"
"query.currentCustomer.frozen"
"query.currentCustomer.invoices"
"query.currentCustomer.statements"
"query.currentCustomer.reminders"
"query.currentCustomer.anonymizationRequest"
"query.currentCustomer.tasks"
"query.currentCustomer.autogiro"
"query.currentCustomer.agreements"
"query.currentOwner.customerRoles"
"query.customers.cards.transactions"
"query.agreements"
"query.currentUserProfile.userProfile"
"query.currentUserProfile.userProfile.identifier"
"query.currentUserProfile.roles"
 * 
 * All Write rights:
"mutation.currentCustomer.addUserProfile"
"mutation.currentCustomer.updateUserProfile"
"mutation.currentCustomer.deleteUserProfile"
"mutation.currentCustomer.updateUserProfileRoles"
"mutation.currentCustomer.logout"
"mutation.currentCustomer.approveAgreement"
"mutation.currentCustomer.updateCardPinCode"
"mutation.currentCustomer.updateCardReferences"
"mutation.currentCustomer.updateCardProductRestrictions"
"mutation.currentCustomer.enableCard"
"mutation.currentCustomer.disableCard"
"mutation.currentCustomer.addCustomerTask"
"mutation.currentCustomer.updateInvoiceDelivery"
"mutation.currentCustomer.updateStatementDelivery"
"mutation.currentCustomer.autogiro"
"mutation.updateCurrentCustomer.email"
"mutation.updateCurrentCustomer.phoneNumber"
"mutation.registerDevice"
"mutation.createReservation" 
 */

type RightsWrapperProps = {
	disableButtonClasses?: string;
	disableButtonIcon?: React.ReactNode;
	children: React.ReactNode;
	requiredRight?: string | string[]; // Specific right(s) needed to show children
	disableButtonText?: string; // Text to show on disabled button
	disableButtonTooltipText?: string; // Tooltip text for disabled button
	onlySuperUser?: boolean; // If true, only show children if user has a "Superuser" role
	forceDisabled?: boolean; // If true, forces the button to be disabled regardless of rights
	notAButton?: boolean; // If true, render a non-button element when rights are insufficient
	toolTipPosition?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'right'
	overrideTooltipText?: string; // If provided, this text will be shown in the tooltip instead of the standard rights-related messages
	ariaLabel?: string; // Optional aria-label for accessibility
	fetchAnimationWidth?: number; // Optional width for the loading animation
	fetchAnimationHeight?: number; // Optional height for the loading animation
	fetchAnimationBorderRadius?: number; // Optional border radius for the loading animation
	fetchingAnimationMarginRight?: number; // Optional margin right for the loading animation
}

const RightsWrapper: React.FC<RightsWrapperProps> = ({
	disableButtonClasses = '',
	children,
	requiredRight,
	disableButtonText = 'Ej behörig',
	disableButtonIcon,
	disableButtonTooltipText = '',
	notAButton = false,
	onlySuperUser = false,
	forceDisabled = false,
	toolTipPosition = 'top',
	overrideTooltipText,
	ariaLabel = '',
	fetchAnimationWidth = 200,
	fetchAnimationHeight = 38,
	fetchAnimationBorderRadius = 9999,
	fetchingAnimationMarginRight = 0
}) => {

	const { data: currentCustomerData, loading: currentCustomerLoading, error: currentCustomerError } = useGetCustomer<CustomerRightsDataResponse>(
		GET_CUSTOMER_RIGHTS_DATA_QUERY,
		{},
		{ fetchPolicy: 'network-only' }
	);

	// Get the available mutation role claimNames from allkort — used to check if user has all mutation rights
	const { roles, loading: rolesLoading, isComplete: rolesComplete } = useGetOwnerCustomerRoles();
	const availableNewStyleWriteClaimNames = useMemo<string[]>(() => {
		if (!rolesComplete || rolesLoading) return [];
		return roles
			.filter(r => r.claimName.includes('mutation'))
			.map(r => r.claimName);
	}, [roles, rolesComplete, rolesLoading]);

	// Get the current user's profile and roles
	const { userProfile, loading: userProfileLoading, error: userProfileError } = useGetCurrentUserProfile();

	const structuredCurrentUserRoles = useMemo<StructuredCurrentUserRoles>(() => {
		const result: StructuredCurrentUserRoles = { other: [], write: [], read: [] };
		if (!userProfile) return result;
		userProfile.roles.forEach(role => {
			if (role.claimName.includes('query')) {
				result.read.push(role);
			} else if (role.claimName.includes('mutation')) {
				result.write.push(role);
			} else {
				result.other.push(role);
			}
		});
		return result;
	}, [userProfile]);

	// Check customer rights based on frozen status and state
	// Customer has NO rights if:
	// 1. Customer is frozen (frozen.is = true), OR
	// 2. Customer has a blocking state (state !== null)
	// Customer ONLY has rights when: NOT frozen AND state is null
	const isFrozen = currentCustomerData?.currentCustomer?.frozen?.is ?? false;
	const hasBlockingState = currentCustomerData?.currentCustomer?.state !== null && currentCustomerData?.currentCustomer?.state !== undefined;

	// Check the user's mutation claimNames.
	const userNewStyleWriteNames = structuredCurrentUserRoles.write
		.filter(r => r.claimName.includes('mutation'))
		.map(r => r.claimName);

	// Redaktör: has all available mutation claimNames.
	const hasAllNewStyleWrite = availableNewStyleWriteClaimNames.length > 0 &&
		availableNewStyleWriteClaimNames.every(name => userNewStyleWriteNames.includes(name));

	const isRedaktör = hasAllNewStyleWrite;
	const hasPartialWriteRights = !isRedaktör && structuredCurrentUserRoles.write.length > 0;
	const isSuperUser = structuredCurrentUserRoles.other.some(role => role.claimName === 'Superuser');
	const requiredRights = requiredRight ? (Array.isArray(requiredRight) ? requiredRight : [requiredRight]) : [];
	const hasSpecificRequiredRights = requiredRights.length === 0 || requiredRights.every(right =>
		userProfile?.roles.some(role => role.claimName === right)
	);
	const hasRequiredRights = !isFrozen && !hasBlockingState && (
		onlySuperUser
			? isSuperUser
			: isSuperUser || (requiredRights.length > 0 ? hasSpecificRequiredRights : isRedaktör)
	);

	let standardTooltipText = isFrozen
		? 'Funktion ej tillgänglig, ditt konto är spärrat. Kontakta kundservice för mer information.'
		: hasBlockingState
			? 'Ditt konto har en begränsande status. Kontakta kundservice för mer information.'
			: hasPartialWriteRights
				? 'Du saknar rättigheter till denna funktion. Be din administratör uppdatera rättigheterna för ditt konto.'
				: 'Du saknar rättigheter till denna funktion.';
	// Show loading state

	if (overrideTooltipText && overrideTooltipText.length > 0) {
		standardTooltipText = overrideTooltipText;
	}

	if (currentCustomerLoading || userProfileLoading) {
		return (
			<FetchingAnimation
				width={fetchAnimationWidth}
				height={fetchAnimationHeight}
				borderRadius={fetchAnimationBorderRadius}
				marginRight={fetchingAnimationMarginRight}
			/> // Or show a skeleton/loading state
		)
	}

	// Handle error state
	if (currentCustomerError || userProfileError) {
		if (currentCustomerError) console.error('Error fetching customer rights:', currentCustomerError);
		if (userProfileError) console.error('Error fetching current user profile:', userProfileError);
		return (
			<FetchingAnimation
				width={fetchAnimationWidth}
				height={fetchAnimationHeight}
				borderRadius={fetchAnimationBorderRadius}
				marginRight={fetchingAnimationMarginRight}
			/> // Or show a skeleton/loading state
		)
	}

	if (forceDisabled) {
		if (!notAButton) {
			return (
				<Tooltip text={disableButtonTooltipText.length > 0 ? disableButtonTooltipText : standardTooltipText} position={toolTipPosition}>
					<div className={disableButtonClasses}>
						{disableButtonIcon !== undefined && disableButtonIcon}
						{disableButtonText}
					</div>
				</Tooltip>
			);
		} else {
			return (
				<Tooltip text={disableButtonTooltipText.length > 0 ? disableButtonTooltipText : standardTooltipText} position={toolTipPosition}>
					<button
						disabled
						className={disableButtonClasses}
						style={{}}
						aria-label={ariaLabel}
					>
						{disableButtonIcon !== undefined && disableButtonIcon}
						{disableButtonText}
					</button>
				</Tooltip>
			);
		}
	}

	// If user has rights, show children
	if (hasRequiredRights) {
		return <>{children}</>;
	}

	// If no rights, show disabled/empty button
	if (notAButton) {
		return (
			<Tooltip text={disableButtonTooltipText.length > 0 ? disableButtonTooltipText : standardTooltipText} position={toolTipPosition}>
				<div className={disableButtonClasses}>
					{disableButtonIcon !== undefined && disableButtonIcon}
					{disableButtonText}
				</div>
			</Tooltip>
		);
	}
	return (
		<Tooltip text={disableButtonTooltipText.length > 0 ? disableButtonTooltipText : standardTooltipText} position={toolTipPosition}>
			<button
				disabled
				className={disableButtonClasses}
				style={{}}
				aria-label={ariaLabel}
			>
				{disableButtonIcon !== undefined && disableButtonIcon}
				{disableButtonText}
			</button>
		</Tooltip>
	);
}

export default RightsWrapper
